/**
 * Command registry for the command palette.
 *
 * `useCommands()` builds the palette's command tree from live app state (auth,
 * organizations, theme). The tree supports arbitrary nesting — a node with
 * `children` opens a submenu, a leaf runs `onSelect`. `group` labels are shown
 * only at the level where they are declared.
 */

import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  Boxes,
  FileText,
  Keyboard,
  LayoutGrid,
  LogOut,
  Magnet,
  Mail,
  Palette,
  Plus,
  Puzzle,
  Users,
  X,
} from "lucide-react";
import { THEMES, useTheme } from "@itoresearch/design-system";

import { useAppAuth } from "../../hooks/use-auth";
import { useMe } from "../../hooks/use-me";
import { useSwitchOrganization } from "../../hooks/use-switch-organization";
import { HOTKEYS, formatHotkey } from "../../lib/hotkeys";
import { createWorkspace } from "../../lib/workspaces";
import {
  closeWorkspaceTab,
  openWorkspaceTab,
  useWorkspaceTabs,
} from "../terminal/tabs-store";
import { openWidgetPicker } from "../terminal/widget-picker-store";
import { useCommandMenu } from "./command-menu";

export interface CommandNode {
  /** Stable unique id. */
  id: string;
  title: string;
  description?: string;
  keywords?: string[];
  icon?: LucideIcon;
  /** Shortcut shown on the right; display-only (registration lives in lib/hotkeys). */
  shortcut?: string;
  /** Submenu entries. */
  children?: CommandNode[];
  /** Group heading label, shown when the node renders as a top-level item. */
  group?: string;
  /** Leaf action. */
  onSelect?: () => void;
}

/** Build the palette's command tree for the current session. */
export function useCommands(): CommandNode[] {
  const navigate = useNavigate();
  const { user, signOut } = useAppAuth();
  const { data: me } = useMe();
  const switchOrg = useSwitchOrganization();
  const { setTheme } = useTheme();
  const { activeId } = useWorkspaceTabs();
  const { openWorkspacePicker } = useCommandMenu();

  return useMemo(() => {
    const organizations = me?.organizations ?? [];

    /** Create a workspace at a layout and open it as a tab. */
    const createAndOpen = (
      kind: "canvas" | "page",
      rigidity: "canvas_grid" | "canvas" | "grid",
      title: string,
    ) => {
      void createWorkspace({ kind, rigidity, title }).then((workspace) =>
        openWorkspaceTab(workspace.workspaceId, {
          title: workspace.title,
          kind: workspace.kind,
        }),
      );
    };

    const workspaceCommands: CommandNode[] = [
      {
        id: "workspace-new",
        group: "Workspaces",
        title: "New tab…",
        description: "Search or create a workspace",
        keywords: ["workspace", "new", "create", "tab", "open"],
        icon: Plus,
        shortcut: formatHotkey(HOTKEYS.newTab),
        onSelect: openWorkspacePicker,
      },
      {
        id: "workspace-new-snapping",
        group: "Workspaces",
        title: "New snapping canvas",
        description: "Free-form board; widgets snap to a grid",
        keywords: ["workspace", "canvas", "new", "board", "snap", "grid"],
        icon: Magnet,
        onSelect: () =>
          createAndOpen("canvas", "canvas_grid", "Untitled canvas"),
      },
      {
        id: "workspace-new-free-canvas",
        group: "Workspaces",
        title: "New free canvas",
        description: "Infinite board; place widgets anywhere",
        keywords: ["workspace", "canvas", "new", "free", "board"],
        icon: LayoutGrid,
        onSelect: () => createAndOpen("canvas", "canvas", "Untitled canvas"),
      },
      {
        id: "workspace-new-grid",
        group: "Workspaces",
        title: "New grid page",
        description: "Structured, auto-laid-out page",
        keywords: ["workspace", "page", "new", "grid", "structured"],
        icon: FileText,
        onSelect: () => createAndOpen("page", "grid", "Untitled page"),
      },
      ...(activeId
        ? [
            {
              id: "workspace-add-widget",
              group: "Widgets",
              title: "Add widget…",
              description: "Pick a widget from your library and configure it",
              keywords: ["widget", "add", "insert", "configure", "component"],
              icon: Puzzle,
              onSelect: openWidgetPicker,
            } satisfies CommandNode,
          ]
        : []),
      {
        id: "workspace-close-tab",
        group: "Workspaces",
        title: "Close current tab",
        description: "Close the active workspace tab",
        keywords: ["workspace", "close", "tab"],
        icon: X,
        shortcut: formatHotkey(HOTKEYS.closeTab),
        onSelect: () => {
          if (activeId) closeWorkspaceTab(activeId);
        },
      },
    ];

    const navigation: CommandNode = {
      id: "go",
      group: "Navigation",
      title: "Go to…",
      description: "Jump to a product surface",
      icon: Boxes,
      children: [
        {
          id: "go-home",
          title: "Home",
          description: "Research terminal",
          icon: LayoutGrid,
          shortcut: formatHotkey(HOTKEYS.goToHome),
          onSelect: () => navigate({ to: "/" }),
        },
        {
          id: "go-box",
          title: "Box",
          description: "Ito mailbox",
          icon: Mail,
          shortcut: formatHotkey(HOTKEYS.goToBox),
          onSelect: () => navigate({ to: "/box" }),
        },
        {
          id: "go-apps",
          title: "Apps",
          description: "Ito compute",
          icon: Boxes,
          shortcut: formatHotkey(HOTKEYS.goToApps),
          onSelect: () => navigate({ to: "/apps" }),
        },
        {
          id: "go-design",
          title: "Design system",
          description: "Component + theme showcase",
          icon: Palette,
          onSelect: () => navigate({ to: "/design" }),
        },
      ],
    };

    const theme: CommandNode = {
      id: "theme",
      group: "Appearance",
      title: "Theme…",
      description: "Change the theme",
      icon: Palette,
      children: THEMES.map((t) => ({
        id: `theme-${t.id}`,
        title: t.label,
        description: `${t.appearance} appearance`,
        keywords: [t.id, t.appearance, "theme", "appearance"],
        icon: Palette,
        onSelect: () => setTheme(t.id),
      })),
    };

    const shortcuts: CommandNode = {
      id: "shortcuts",
      group: "Help",
      title: "Keyboard shortcuts",
      description: "Every hotkey registered in the app",
      icon: Keyboard,
      onSelect: undefined, // rendered specially in the palette
    };

    const account: CommandNode | null = user
      ? {
          id: "account",
          group: "Account",
          title: "Account…",
          description: user.email,
          icon: Users,
          children: [
            ...(organizations.length > 1
              ? [
                  {
                    id: "switch-org",
                    title: "Switch organization",
                    description: "Change your active organization",
                    icon: Users,
                    children: organizations.map((org) => ({
                      id: `org-${org.id}`,
                      title: org.name,
                      description: org.role ?? undefined,
                      onSelect: () => switchOrg.mutate(org.id),
                    })),
                  },
                ]
              : []),
            {
              id: "sign-out",
              title: "Sign out",
              description: "End the session",
              icon: LogOut,
              onSelect: () => signOut(),
            },
          ],
        }
      : null;

    return [
      navigation,
      ...workspaceCommands,
      theme,
      shortcuts,
      ...(account ? [account] : []),
    ];
  }, [
    navigate,
    user,
    me,
    signOut,
    setTheme,
    switchOrg,
    activeId,
    openWorkspacePicker,
  ]);
}
